import React, { useState } from 'react';
import { Shield, FileText, Copyright, Award, ArrowRight } from 'lucide-react';
import RegistrationModal from './RegistrationModal';

const WhatIsTrademarkSection = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const types = [
    {
      icon: FileText,
      title: 'Word Marks',
      description: 'Brand names, slogans, and text-based identifiers that distinguish your products or services'
    },
    {
      icon: Copyright,
      title: 'Design Marks',
      description: 'Logos, symbols, colors, and visual elements that represent your brand identity'
    },
    {
      icon: Shield,
      title: 'Service Marks',
      description: 'Identifiers specifically for services rather than physical products'
    },
    {
      icon: Award,
      title: 'Collective Marks',
      description: 'Marks used by members of a collective group or organization'
    }
  ];

  return (
    <div className="relative py-20 overflow-hidden">
      {/* Background with gradient and pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-purple-50/30 to-pink-50/30">
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, #FF035B 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section - Centered */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#FF035B] to-[#2F1A8E] text-white px-6 py-2 rounded-full text-sm font-semibold mb-6 shadow-lg">
            <Shield className="w-4 h-4" />
            Understanding Trademarks
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#404138] mb-6">
            What is a Trademark?
          </h2>
          <p className="text-xl text-[#6E6B6A] max-w-3xl mx-auto leading-relaxed">
            Your brand's legal fingerprint - protecting what makes your business unique and valuable
          </p>
        </div>

        {/* Main Content - New Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          {/* Main Explanation Card - Spans 2 columns */}
          <div className="lg:col-span-2 bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl p-10 border border-[#FF035B]/20">
            <div className="space-y-6 text-lg text-[#6E6B6A] leading-relaxed">
              <p>
                A trademark is a <span className="font-bold text-[#FF035B]">distinctive sign, symbol, word, phrase, logo, or design</span> that identifies and distinguishes the source of goods or services of one party from those of others. It's essentially your brand's fingerprint in the marketplace.
              </p>
              <p>
                Think of iconic brands like <span className="font-semibold text-[#404138]">Nike's swoosh, Apple's apple, or McDonald's golden arches</span>. These trademarks are instantly recognizable and legally protected, preventing others from using confusingly similar marks.
              </p>
              <p>
                In the United States, trademark rights are established through either <span className="font-semibold text-[#404138]">actual use in commerce</span> or by filing an application with the <span className="font-semibold text-[#404138]">United States Patent and Trademark Office (USPTO)</span>. Once registered, a trademark can last indefinitely as long as it continues to be used and properly maintained.
              </p>
              <div className="bg-gradient-to-r from-[#FF035B]/5 to-[#2F1A8E]/5 border-l-4 border-[#FF035B] p-6 rounded-r-xl">
                <p className="font-bold text-[#404138] text-xl">
                  A strong trademark not only protects your brand legally but also builds customer trust, brand recognition, and business value over time.
                </p>
              </div>
            </div>
          </div>

          {/* Types Card - Single column */}
          <div className="bg-gradient-to-br from-[#2F1A8E] to-[#FF035B] rounded-3xl shadow-2xl p-8 text-white">
            <h3 className="text-lg font-bold mb-8 text-center">Types of Trademarks</h3>
            <div className="space-y-6">
              {types.map((type, index) => (
                <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:bg-white/20 transition-all duration-300">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center border border-white/30">
                        <type.icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <h4 className="font-bold text-white mb-2">{type.title}</h4>
                      <p className="text-white/80 text-sm leading-relaxed">{type.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Enhanced CTA Banner - Full Width */}
        <div className="relative bg-gradient-to-br from-[#2F1A8E] via-[#1a1a3e] to-black rounded-3xl shadow-2xl p-12 overflow-hidden border border-[#FF035B]/30">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#FF035B]/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#2F1A8E]/20 rounded-full blur-3xl"></div>
          
          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex-1 text-center md:text-left">
              <div className="inline-flex items-center gap-2 bg-[#FF035B]/20 border border-[#FF035B]/40 px-4 py-2 rounded-full mb-4">
                <Shield className="w-4 h-4 text-[#FF035B]" />
                <span className="text-sm font-semibold text-white">Limited Time - $45 Only</span>
              </div>
              <h4 className="text-2xl md:text-3xl font-bold text-white mb-3">
                Ready to Protect Your Brand?
              </h4>
              <p className="text-xl text-gray-300 mb-2">
                Start your trademark registration today and secure your competitive advantage
              </p>
              <div className="flex items-center justify-center md:justify-start gap-4 text-sm text-gray-400 mt-4">
                <span className="flex items-center gap-1">
                  ✓ 98% Success Rate
                </span>
                <span className="flex items-center gap-1">
                  ✓ 24-48h Filing
                </span>
                <span className="flex items-center gap-1">
                  ✓ Money-Back Guarantee
                </span>
              </div>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="group bg-gradient-to-r from-[#FF035B] via-[#FF1493] to-[#FF035B] hover:from-[#FF1493] hover:to-[#FF035B] text-white px-10 py-5 rounded-xl font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-2xl shadow-[#FF035B]/40 flex items-center gap-3"
            >
              Get Started Now
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </div>

      {/* Registration Modal */}
      <RegistrationModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
    </div>
  );
};

export default WhatIsTrademarkSection;